import React, { useState, useEffect } from "react";
import {
  FaChalkboardTeacher,
  FaMapMarkedAlt,
  FaUserTie,
  FaCrown,
  FaBriefcase,
  FaCalendarAlt,
  FaBuilding,
} from "react-icons/fa";
import { Link } from "react-router-dom";

const experience = [
  {
    year: "2010–2011",
    role: "Teacher",
    company: "Matri Sisu Vidyalaya",
    type: "Education",
    description:
      "Started my professional journey in education, teaching and mentoring young minds.",
    icon: FaChalkboardTeacher,
    color: "from-green-500 to-emerald-500",
    period: "1 Year",
  },
  {
    year: "2011–2013 ",
    role: "Senior Teacher",
    company: "Welhome",
    type: "Education  ",
    description:
      "Expanded my teaching expertise and developed curriculum for diverse student groups.",
    icon: FaChalkboardTeacher,
    color: "from-blue-500 to-cyan-500",
    period: "2 Years",
  },
  {
    year: "2013–2014",
    role: "Tour Operator",
    company: "Adventure Himalaya Circuit Treks & Expeditions",
    type: "Tourism",
    description:
      "Transitioned into tourism industry, organizing and leading adventure expeditions.",
    icon: FaMapMarkedAlt,
    color: "from-orange-500 to-red-500",
    period: "1 Year",
  },
  {
    year: "2014–2015",
    role: "Director",
    company: "Adventure Maypole Treks & Expeditions",
    type: "Leadership",
    description:
      "Took on leadership role, managing operations and strategic planning for trek expeditions.",
    icon: FaUserTie,
    color: "from-purple-500 to-violet-500",
    period: "1 Year",
  },
  {
    year: "2015–Present",
    role: "Managing Director , CEO",
    company: "Top of the World Adventure Pvt. Ltd.",
    type: "Entrepreneurship",
    description:
      " Founded and Leading a  Nepal's premier adventure tourism company, driving innovation and sustainable practices.",
    icon: FaCrown,
    color: "from-yellow-500 to-orange-500",
    period: "9+ Years",
  },
];

const milestones = [
  { year: "2010", event: "Language Courses", icon: "🌐" },
  { year: "2013", event: "Entered Tourism Industry", icon: "⛰️" },
  { year: "2014", event: "Became  Director", icon: "👑" },
  { year: "2015", event: "Founded Own Company", icon: "🚀" },
  { year: "2025", event: "10+ Years Experience", icon: "🏆" },
];

const skills = [
  { name: "Leadership & Management", level: 95, category: "Management" },
  { name: "Tourism Operations", level: 98, category: "Industry" },
  { name: "Cultural Communication", level: 92, category: "Soft Skills" },
  { name: "Business Strategy", level: 88, category: "Strategy" },
  { name: "Team Building", level: 94, category: "Management" },
  { name: "Customer Relations", level: 96, category: "Service" },
];

export default function Experience() {
  const [visibleItems, setVisibleItems] = useState([]);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleItems((prev) => [...prev, entry.target.dataset.index]);
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll(".experience-item").forEach((item, index) => {
      item.dataset.index = index;
      observer.observe(item);
    });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      observer.disconnect();
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900">
      {/* Hero Section */}
      <div className="relative pt-24 pb-16 px-6 lg:px-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="text-6xl md:text-8xl font-black mb-6 bg-gradient-to-r from-blue-400 via-cyan-400 to-teal-400 bg-clip-text text-transparent">
              Experience
            </h1>
            <div className="w-32 h-1 bg-gradient-to-r from-blue-500 to-cyan-500 mx-auto mb-8 rounded-full"></div>
            <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              A journey from education to entrepreneurship, building bridges
              between cultures through adventure tourism and sustainable travel
              experiences.
            </p>
          </div>

          {/* Milestones */}
          <div className="flex flex-wrap justify-center gap-6 mb-16">
            {milestones.map((milestone, index) => (
              <div
                key={index}
                className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-cyan-500/50 transition-all duration-300 hover:scale-105 text-center"
              >
                <div className="text-3xl mb-2">{milestone.icon}</div>
                <div className="text-sm font-bold text-cyan-400">
                  {milestone.year}
                </div>
                <div className="text-sm text-gray-300">{milestone.event}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Experience Timeline */}
      <div className="px-6 lg:px-20 py-16">
        <div className="max-w-6xl mx-auto">
          <div className="relative">
            {/* Timeline Items */}
            <div className="space-y-12">
              {experience.map((exp, index) => (
                <div
                  key={index}
                  className={`experience-item relative transition-all duration-1000 ${
                    visibleItems.includes(index.toString())
                      ? "opacity-100 translate-x-0"
                      : `opacity-0 ${
                          index % 2 === 0 ? "-translate-x-8" : "translate-x-8"
                        }`
                  }`}
                >
                  <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/10 hover:border-cyan-500/50 transition-all duration-300 hover:scale-[1.02] group">
                    <div className="grid lg:grid-cols-4 gap-6 items-center">
                      {/* Icon and Period */}
                      <div className="lg:col-span-1 text-center lg:text-left">
                        <div
                          className={`inline-flex w-16 h-16 bg-gradient-to-r ${exp.color} rounded-2xl items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}
                        >
                          <exp.icon className="w-8 h-8 text-white" />
                        </div>
                        <div className="space-y-1">
                          {/* <div className="text-sm font-bold text-cyan-400">
                            {exp.period}
                          </div> */}
                          {/* <div className="text-xs text-gray-400 px-2 py-1 bg-white/10 rounded-full">
                            {exp.type}
                          </div> */}
                        </div>
                      </div>

                      {/* Main Content */}
                      <div className="lg:col-span-3 space-y-4">
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                          <div>
                            <h3 className="text-2xl font-bold text-white group-hover:text-cyan-300 transition-colors">
                              {exp.role}
                            </h3>
                            <div className="flex items-center space-x-2 mt-1">
                              <FaBuilding className="w-4 h-4 text-cyan-400" />
                              <span className="text-lg font-medium text-cyan-400">
                                {exp.company}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2 mt-2 lg:mt-0">
                            <FaCalendarAlt className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-400 font-medium">
                              {exp.year}
                            </span>
                          </div>
                        </div>

                        <p className="text-gray-300 leading-relaxed">
                          {exp.description}
                        </p>

                        <div className="flex flex-wrap gap-2 pt-2">
                          <span className="px-3 py-1 bg-cyan-500/20 text-cyan-300 rounded-full text-sm font-medium">
                            {exp.type}
                          </span>
                          {exp.role.includes("Director") && (
                            <span className="px-3 py-1 bg-yellow-500/20 text-yellow-300 rounded-full text-sm font-medium">
                              Leadership
                            </span>
                          )}
                          {exp.company.includes("Adventure") && (
                            <span className="px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-sm font-medium">
                              Adventure Tourism
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Skills Section */}
      <div className="px-6 lg:px-20 py-16">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Core Competencies
            </h2>
            <p className="text-xl text-gray-300">
              Skills developed through years of diverse experience
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {skills.map((skill, index) => (
              <div
                key={index}
                className="group bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-cyan-500/50 transition-all duration-300"
              >
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-white group-hover:text-cyan-300 transition-colors">
                      {skill.name}
                    </h3>
                    <span className="text-sm text-gray-400">
                      {skill.category}
                    </span>
                  </div>
                  <span className="text-2xl font-bold text-cyan-400">
                    {skill.level}%
                  </span>
                </div>

                <div className="relative">
                  <div className="w-full bg-gray-700 rounded-full h-3 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full transition-all duration-1500 ease-out"
                      style={{
                        width: visibleItems.includes(index.toString())
                          ? `${skill.level}%`
                          : "0%",
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div className="px-6 lg:px-20 py-16">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-12 border border-white/10">
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Create Your Adventure?
            </h3>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              With over 15 years of experience in tourism and education, I'm
              here to make your travel dreams a reality.
            </p>
            <Link
              to="https://topoftheworldadv.com/"
              target="_blank"
              rel="noreferrer"
            >
              <button className="px-8 py-4 bg-gradient-to-r from-cyan-600 to-blue-600 rounded-full font-semibold text-white hover:from-blue-600 hover:to-cyan-600 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-cyan-500/25">
                <span className="flex items-center space-x-2">
                  <FaBriefcase className="w-5 h-5" />
                  <span>Let's Travel Together</span>
                </span>
              </button>
            </Link>
          </div>
        </div>
      </div>

      {/* Background Elements */}
      <div className="fixed inset-0 pointer-events-none opacity-5">
        <div
          className="absolute w-96 h-96 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 rounded-full blur-3xl"
          style={{
            transform: `translate(${scrollY * 0.1}px, ${scrollY * 0.05}px)`,
            left: "20%",
            top: "10%",
          }}
        ></div>
        <div
          className="absolute w-96 h-96 bg-gradient-to-r from-blue-500/20 to-teal-500/20 rounded-full blur-3xl"
          style={{
            transform: `translate(-${scrollY * 0.1}px, -${scrollY * 0.05}px)`,
            right: "20%",
            bottom: "10%",
          }}
        ></div>
      </div>
    </div>
  );
}
